import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Case } from '../../api/caseApi';

interface CaseState {
  cases: Case[];
  selectedCase: Case | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    status: string[];
    priority: string[];
    practice: string[];
    assignedTo: string[];
    tags?: string[];
    dateRange?: {
      start: string;
      end: string;
    };
    searchTerm?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  sortBy: {
    field: string;
    direction: 'asc' | 'desc';
  };
}

const initialState: CaseState = {
  cases: [],
  selectedCase: null,
  isLoading: false,
  error: null,
  filters: {
    status: [],
    priority: [],
    practice: [],
    assignedTo: [],
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  sortBy: {
    field: 'updatedAt',
    direction: 'desc',
  },
};

const caseSlice = createSlice({
  name: 'cases',
  initialState,
  reducers: {
    fetchCasesStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    fetchCasesSuccess(state, action: PayloadAction<{ cases: Case[]; total: number }>) {
      state.isLoading = false;
      state.cases = action.payload.cases;
      state.pagination.total = action.payload.total;
    },
    fetchCasesFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    fetchCaseStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    fetchCaseSuccess(state, action: PayloadAction<Case>) {
      state.isLoading = false;
      state.selectedCase = action.payload;
    },
    fetchCaseFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    createCaseStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    createCaseSuccess(state, action: PayloadAction<Case>) {
      state.isLoading = false;
      state.cases = [action.payload, ...state.cases];
      state.pagination.total += 1;
    },
    createCaseFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    updateCaseStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    updateCaseSuccess(state, action: PayloadAction<Case>) {
      state.isLoading = false;
      state.cases = state.cases.map(c => (c.id === action.payload.id ? action.payload : c));
      if (state.selectedCase && state.selectedCase.id === action.payload.id) {
        state.selectedCase = action.payload;
      }
    },
    updateCaseFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    deleteCaseStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    deleteCaseSuccess(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.cases = state.cases.filter(c => c.id !== action.payload);
      if (state.selectedCase && state.selectedCase.id === action.payload) {
        state.selectedCase = null;
      }
      state.pagination.total -= 1;
    },
    deleteCaseFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setFilters(state, action: PayloadAction<Partial<CaseState['filters']>>) {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    clearFilters(state) {
      state.filters = {
        status: [],
        priority: [],
        practice: [],
        assignedTo: [],
      };
      state.pagination.page = 1;
    },
    setPagination(state, action: PayloadAction<Partial<CaseState['pagination']>>) {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setSortBy(state, action: PayloadAction<CaseState['sortBy']>) {
      state.sortBy = action.payload;
    },
    selectCase(state, action: PayloadAction<string | null>) {
      if (action.payload === null) {
        state.selectedCase = null;
      } else {
        const foundCase = state.cases.find(c => c.id === action.payload);
        if (foundCase) {
          state.selectedCase = foundCase;
        }
      }
    },
    clearSelectedCase(state) {
      state.selectedCase = null;
    },
    addCaseNote(state, action: PayloadAction<{ caseId: string; note: Case['notes'][0] }>) {
      const { caseId, note } = action.payload;
      const caseIndex = state.cases.findIndex(c => c.id === caseId);
      if (caseIndex !== -1) {
        state.cases[caseIndex].notes = [note, ...state.cases[caseIndex].notes];
      }
      if (state.selectedCase && state.selectedCase.id === caseId) {
        state.selectedCase.notes = [note, ...state.selectedCase.notes];
      }
    },
    addCaseDocument(
      state,
      action: PayloadAction<{ caseId: string; document: Case['documents'][0] }>
    ) {
      const { caseId, document } = action.payload;
      const caseIndex = state.cases.findIndex(c => c.id === caseId);
      if (caseIndex !== -1) {
        state.cases[caseIndex].documents = [document, ...state.cases[caseIndex].documents];
      }
      if (state.selectedCase && state.selectedCase.id === caseId) {
        state.selectedCase.documents = [document, ...state.selectedCase.documents];
      }
    },
    addCaseDeadline(
      state,
      action: PayloadAction<{ caseId: string; deadline: Case['deadlines'][0] }>
    ) {
      const { caseId, deadline } = action.payload;
      const caseIndex = state.cases.findIndex(c => c.id === caseId);
      if (caseIndex !== -1) {
        state.cases[caseIndex].deadlines = [deadline, ...state.cases[caseIndex].deadlines];
      }
      if (state.selectedCase && state.selectedCase.id === caseId) {
        state.selectedCase.deadlines = [deadline, ...state.selectedCase.deadlines];
      }
    },
    updateCaseStatus(state, action: PayloadAction<{ caseId: string; status: Case['status'] }>) {
      const { caseId, status } = action.payload;
      const caseIndex = state.cases.findIndex(c => c.id === caseId);
      if (caseIndex !== -1) {
        state.cases[caseIndex].status = status;
      }
      if (state.selectedCase && state.selectedCase.id === caseId) {
        state.selectedCase.status = status;
      }
    },
    clearCaseError(state) {
      state.error = null;
    },
  },
});

export const {
  fetchCasesStart,
  fetchCasesSuccess,
  fetchCasesFailure,
  fetchCaseStart,
  fetchCaseSuccess,
  fetchCaseFailure,
  createCaseStart,
  createCaseSuccess,
  createCaseFailure,
  updateCaseStart,
  updateCaseSuccess,
  updateCaseFailure,
  deleteCaseStart,
  deleteCaseSuccess,
  deleteCaseFailure,
  setFilters,
  clearFilters,
  setPagination,
  setSortBy,
  selectCase,
  clearSelectedCase,
  addCaseNote,
  addCaseDocument,
  addCaseDeadline,
  updateCaseStatus,
  clearCaseError,
} = caseSlice.actions;

export default caseSlice.reducer;
